import { useEffect, useMemo, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';

import { leadCapture } from 'api/jalapeno';
import useCustomerExistsQuery from 'api/queries/useCustomerExistsQuery';
import useValidDeliveryDaysQuery from 'api/queries/useValidDeliveryDaysNoSubQuery';
import {
    AP_FEATURE_FLAG,
    AP_LOCALSTORAGE_KEY,
    AP_MODULES_AND_PAGE_TREATMENT,
    AP_MODULES_TREATMENT,
    AP_OFF_TREATMENT,
    AP_PAGE_ORGANIC,
} from 'constants/featureFlags/assortmentPreview';
import { MIN_SIGNUP_TREATMENT } from 'constants/featureFlags/boysenberrySignup';
import { QUIZ_PATH, SignupQuizSteps } from 'constants/signup';
import Button from 'honeydew/Button';
import TextField from 'honeydew/TextField';
import useBrandLinks from 'hooks/useBrandLinks';
import { useBoysenberrySignupFlag } from 'hooks/useFeatureFlag/useBoysenberrySignupFlag';
import { useDietaryPreferencesFeatureFlag } from 'hooks/useFeatureFlag/useDietaryPreferencesFeatureFlag';
import { useInAssortmentPreviewFlag } from 'hooks/useFeatureFlag/useInAssortmentPreviewFlag';
import { usePlusMembershipSignupFeatureFlag } from 'hooks/useFeatureFlag/usePlusMembershipSignupFeatureFlag';
import { useTrackSecretAmplitudeExperiment } from 'hooks/useFeatureFlag/useTrackSecretAmplitudeExperiment';
import useGlobalStore from 'hooks/useGlobalStore';
import useSignupRedirect from 'hooks/useSignupRedirect';
import useTailwindTheme from 'hooks/useTailwindTheme';
import useTrackPageView from 'hooks/useTrackPageView';
import SignupFooter from 'pages/signup/shared/SignupFooter';
import AssortmentPreviewModule from 'shared/AssortmentPreviewModule/AssortmentPreviewModule';
import { isZipValidFormat } from 'utils/addressUtils';
import { setUserId, trackEvent, trackExperimentAssignment, updateProfile } from 'utils/analyticsUtils';
import { validEmail } from 'utils/emailUtils';
import { getIsSisterBrand } from 'utils/loginUtils';
import {
    getExistingCustomerError,
    getQuizStepsOrdered,
    getSignupExperimentString,
    getSignupLocalStorage,
    setSignupLocalStorage,
} from 'utils/signupUtils';

import CustomerReviews from '../CustomerReviews';
import HowItWorks from '../HowItWorks';
import LeadCapFAQS from '../LeadCapFAQs';
import ProductPreviewWall from '../ProductPreviewWall';

function getCTAText({ noAvailableDays, zipWaitListed }) {
    if (noAvailableDays && !zipWaitListed) {
        return 'Join waitlist';
    }

    if (noAvailableDays && zipWaitListed) {
        return 'Joined waitlist';
    }

    return 'Continue';
}

function Form() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const signupLocalStorage = getSignupLocalStorage();
    const { authenticationPage, privacyPolicy, terms } = useBrandLinks();
    const { isImperfectTheme, isMisfitsTheme } = useGlobalStore();

    const { isOnTreatment: inPlusMembershipSignupOnTreatment } = usePlusMembershipSignupFeatureFlag();

    const tailwindTheme = useTailwindTheme();
    const isSmallScreen = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    const [email, setEmail] = useState({
        error: false,
        valid: signupLocalStorage?.email ? validEmail(signupLocalStorage?.email) : false,
        value: signupLocalStorage?.email ?? '',
        isEditing: false,
    });

    const [zip, setZip] = useState({
        error: false,
        waitListed: null,
        valid: signupLocalStorage?.zip ? isZipValidFormat(signupLocalStorage?.zip) : false,
        value: signupLocalStorage?.zip ?? '',
    });

    const [custExistsError, setCustExistsError] = useState(null);
    const [disableContinue, setDisableContinue] = useState(false);

    const customerExistsQuery = useCustomerExistsQuery({
        email: email.value,
        extendedQueryKeys: ['signup', 'lead-capture'],
        queryProps: { enabled: false },
    });

    const validDeliveryDaysQuery = useValidDeliveryDaysQuery({
        extendedQueryKeys: ['signup'],
        funnelType: isMisfitsTheme ? 'misfits' : 'imperfect',
        queryProps: { enabled: false },
        zip: zip.value,
        orderInterval: signupLocalStorage?.orderInterval,
    });

    const { data: customerExists } = customerExistsQuery;
    const { data: validDeliveryDays } = validDeliveryDaysQuery;

    const amplitudeBrandSetting = useMemo(
        () => (isMisfitsTheme ? 'misfits_market' : 'imperfect_foods'),
        [isMisfitsTheme]
    );

    useEffect(() => {
        updateProfile({ brand: amplitudeBrandSetting });
    }, [amplitudeBrandSetting]);

    const amplitudeZipData = useMemo(
        () => ({
            zipcode: validDeliveryDays?.zip,
            delivery_day: validDeliveryDays?.availableDays?.[0]?.deliveryDay,
            is_deliverable: validDeliveryDays?.availableDays?.length > 0,
            dbi: !!validDeliveryDays?.availableDays?.[0]?.isDeliveredByImperfect,
            in_custo: validDeliveryDays?.availableDays?.[0]?.buildWindow,
            order_min: validDeliveryDays?.availableDays?.[0]?.orderMinimum,
        }),
        [JSON.stringify(validDeliveryDays)]
    );

    const noAvailableDays = zip.value === validDeliveryDays?.zip && validDeliveryDays?.availableDays?.length < 1;
    const dbi = !!validDeliveryDays?.availableDays?.[0]?.isDeliveredByImperfect;

    const {
        key: splitKey,
        splitIsReady: boysenberrySignupSplitIsReady,
        treatment: bosyenberrySignupTreatment,
    } = useBoysenberrySignupFlag();

    const {
        apLocalStorage,
        inAssortmentPreview,
        inPageOnlyTreatment,
        splitLoadedCorrectly: assortmentPreviewSplitIsReady,
        treatment: assortmentPreviewTreatment,
    } = useInAssortmentPreviewFlag();

    const inAssortmentPreviewSplitTreatment =
        assortmentPreviewTreatment === AP_MODULES_TREATMENT ||
        assortmentPreviewTreatment === AP_MODULES_AND_PAGE_TREATMENT;

    const { isOnTreatment: inDietaryPreferencesTreatment, treatment: dietaryPreferencesTreatment } =
        useDietaryPreferencesFeatureFlag();

    const assortmentPreviewSplitOff = assortmentPreviewTreatment === AP_OFF_TREATMENT;

    const inMinSignupTreatment = bosyenberrySignupTreatment === MIN_SIGNUP_TREATMENT;

    const splitLoadedCorrectly = assortmentPreviewSplitIsReady && boysenberrySignupSplitIsReady;

    const treatment = inDietaryPreferencesTreatment ? dietaryPreferencesTreatment : bosyenberrySignupTreatment;

    const trackLeadCaptureEvent = () => {
        let emailRecognized = 'no';
        if (customerExists?.subExists) {
            emailRecognized = customerExists?.brand === 'imperfect_foods' ? 'if_customer' : 'mm_customer';
        } else if (customerExists?.exists) {
            emailRecognized = customerExists?.brand === 'imperfect_foods' ? 'if_prospect' : 'mm_prospect';
        }

        trackEvent('lead capture complete', {
            zipcode: validDeliveryDays?.zip,
            delivery_day: validDeliveryDays?.availableDays?.[0]?.deliveryDay,
            is_deliverable: validDeliveryDays?.availableDays?.length > 0,
            dbi,
            in_custo: validDeliveryDays?.availableDays?.[0]?.buildWindow,
            order_min: validDeliveryDays?.availableDays?.[0]?.orderMinimum,
            brand: isMisfitsTheme ? 'misfits_market' : 'imperfect_foods',
            isNative: !!window.isNativeApp,
            email: email.value,
            domain: window.location.hostname,
            pathname,
            customer_exists: emailRecognized,
            signup_experiment: getSignupExperimentString({
                treatment,
                dbi,
            }),
            split_key: splitKey,
            free_trial_eligible: inPlusMembershipSignupOnTreatment,
            membership_status: inPlusMembershipSignupOnTreatment ? 'eligible' : 'ineligible',
        });
    };

    useEffect(() => {
        updateProfile({ membership_status: inPlusMembershipSignupOnTreatment ? 'eligible' : 'ineligible' });
    }, [inPlusMembershipSignupOnTreatment]);

    useEffect(() => {
        if (!splitLoadedCorrectly) return;

        trackExperimentAssignment({
            experiment: AP_FEATURE_FLAG,
            variation:
                inAssortmentPreviewSplitTreatment || assortmentPreviewSplitOff
                    ? assortmentPreviewTreatment
                    : AP_PAGE_ORGANIC,
        });

        if (!apLocalStorage) {
            localStorage.setItem(AP_LOCALSTORAGE_KEY, assortmentPreviewTreatment);
        }
    }, [splitLoadedCorrectly]);

    const leadCaptureMutation = useMutation({
        mutationFn: (source) =>
            leadCapture({
                sendParams: {
                    email: email.value,
                    experiement: undefined,
                    funnelType: isMisfitsTheme ? 'misfits' : 'imperfect',
                    source,
                    zip: zip.value,
                },
            }),
        onSuccess: (data) => {
            setZip({ ...zip, waitListed: zip.value });

            const id = data?.data?.id;

            setUserId(id);
        },
    });

    useSignupRedirect({ allowedPaths: ['/join/quiz/*'] });
    useTrackPageView('lead capture page view', true);
    useTrackSecretAmplitudeExperiment();

    useEffect(() => {
        if (validDeliveryDays?.zip) {
            // eslint-disable-next-line camelcase
            const { in_custo, order_min, ...otherAmplitudeProperties } = amplitudeZipData;

            trackEvent('zip code enter', {
                ...otherAmplitudeProperties,
                brand: amplitudeBrandSetting,
            });
        }
    }, [validDeliveryDays?.zip]);

    useEffect(() => {
        if (!email.isEditing && email.valid) {
            const formattedEmail = email.value?.trim().toLowerCase();
            trackEvent('email enter', {
                email: formattedEmail,
                location: 'lead capture page',
            });
            updateProfile({ email: formattedEmail });
        }
    }, [email.value, email.isEditing]);

    useEffect(() => {
        if (zip.valid) {
            validDeliveryDaysQuery.refetch();
        }
    }, [zip.valid]);

    useEffect(() => {
        if (!email.valid || !customerExistsQuery.isFetchedAfterMount) {
            return;
        }

        if (customerExistsQuery.isError) {
            console.error(customerExistsQuery.error);
            setDisableContinue(false);
            return;
        }

        if (customerExists.subExists) {
            trackLeadCaptureEvent();
            setDisableContinue(false);
            return;
        }

        const signupLocalStorageData = {
            email: email.value,
            zip: zip.value,
            dbi,
            signupExperiment: getSignupExperimentString({
                treatment,
                dbi,
            }),
        };

        if (!customerExists.subExists) {
            trackLeadCaptureEvent();
            if (inDietaryPreferencesTreatment) {
                const householdMealsQuizStepPath = `${QUIZ_PATH}/${SignupQuizSteps.HOUSEHOLD_MEALS}`;
                setSignupLocalStorage({
                    ...signupLocalStorageData,
                    path: householdMealsQuizStepPath,
                });
                navigate(householdMealsQuizStepPath);
            } else if (inMinSignupTreatment) {
                const summaryQuizStepPath = `${QUIZ_PATH}/${SignupQuizSteps.SUMMARY}`;
                setSignupLocalStorage({
                    ...signupLocalStorageData,
                    path: summaryQuizStepPath,
                });
                navigate(summaryQuizStepPath);
            } else {
                const quizSteps = getQuizStepsOrdered(bosyenberrySignupTreatment);
                const nextQuizStepPath = `/join/quiz/${quizSteps[0]}`;
                setSignupLocalStorage({
                    ...signupLocalStorageData,
                    path: nextQuizStepPath,
                });
                navigate(nextQuizStepPath);
            }
        }
    }, [customerExistsQuery.isFetching, email.valid]);

    useEffect(() => {
        if (customerExistsQuery?.fetchStatus === 'fetching') {
            return;
        }

        if (customerExists?.subExists) {
            const emailFormatted = email.value?.trim().toLowerCase();
            const isSisterBrand = getIsSisterBrand(emailFormatted, customerExists?.brand, isImperfectTheme);

            const existingCustomerError = getExistingCustomerError({
                isImperfectTheme,
                isSisterBrand,
            });

            setCustExistsError(existingCustomerError);
        }
    }, [customerExistsQuery?.fetchStatus, isMisfitsTheme]);

    const handleChangeEmail = (e) => {
        const { value } = e.target;
        setEmail({
            ...email,
            valid: validEmail(value),
            value,
        });

        setZip({
            ...zip,
            waitListed: null,
        });

        setCustExistsError(null);
    };

    const handleChangeZip = (e) => {
        const { value } = e.target;
        if (/^[0-9]*$/.test(value)) {
            setZip({
                ...zip,
                valid: isZipValidFormat(value),
                value,
                waitListed: null,
            });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        setDisableContinue(true);
        customerExistsQuery.refetch();

        if (noAvailableDays && zip.value === zip.waitListed) {
            return;
        }

        if (noAvailableDays && zip.value !== zip.waitListed) {
            const source =
                validDeliveryDays?.days.length >= 1 && validDeliveryDays?.availableDays.length < 1
                    ? 'capped waitlist'
                    : 'waitlist';

            // Currently returns a 400 if a lead has already been captured for an email.
            // This behavior is in parity with Mango.
            leadCaptureMutation.mutate(source);
            return;
        }

        updateProfile({ email: email.value });

        if (!customerExists?.exists) {
            leadCaptureMutation.mutate();
        }
    };

    const ctaText = getCTAText({ noAvailableDays, zipWaitListed: zip?.zipWaitListed });

    return (
        <main>
            <section className="flex flex-col items-center bg-white p-25">
                <form onSubmit={handleSubmit} className="flex max-w-[500px] flex-col">
                    <h1 className="text-body-lg mb-10 font-grotesk-bold md:text-center">
                        Welcome! Let’s see what’s shipping in your area
                    </h1>
                    <TextField
                        id="zip-field"
                        name="zip-code"
                        label="Zip code"
                        placeholder="Enter a 5-digit zip code"
                        maxLength={5}
                        pattern="[0-9]*"
                        value={zip.value}
                        error={zip.error}
                        helperText={zip.error ? 'Please enter a valid zip code' : null}
                        onChange={handleChangeZip}
                        onBlur={() => setZip({ ...zip, error: !zip.valid })}
                        onFocus={() => setZip({ ...zip, error: false })}
                        required
                    />
                    {noAvailableDays && zip.value !== zip.waitListed && (
                        <p className="text-body-md mb-10 font-grotesk-bold text-error">
                            Unfortunately we&apos;re not in your area. Yet! Chances are we&apos;re headed your way soon.
                            You&apos;ll be the first to know when we do.
                        </p>
                    )}
                    <TextField
                        id="email-field"
                        inputProps={{ autoCapitalize: 'off' }}
                        name="email"
                        label="Email"
                        placeholder="Email"
                        value={email.value}
                        error={email.error}
                        helperText={email.error ? 'Please enter a valid email' : null}
                        onChange={handleChangeEmail}
                        onBlur={() => setEmail({ ...email, error: !email.valid, isEditing: false })}
                        onFocus={() => setEmail({ ...email, error: false, isEditing: true })}
                        required
                    />
                    {!!custExistsError && (
                        <p className="text-body-md mb-10 font-grotesk-bold text-error">
                            {custExistsError.message}{' '}
                            {!window.isNativeApp && (
                                <Button as="a" to={custExistsError.signInRoute} className="inline-block underline">
                                    Sign in
                                </Button>
                            )}
                        </p>
                    )}
                    <p className="text-body-xs mb-10 text-center">
                        By providing your email address, you agree to receive marketing emails and agree to our{' '}
                        <Button
                            {...(!window.isNativeApp && { as: 'a' })}
                            to={terms}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block underline"
                            onClick={() => {
                                if (window.isNativeApp) {
                                    window.ReactNativeWebView?.postMessage(
                                        JSON.stringify({
                                            navigate: 'SignupWebview',
                                            url: terms,
                                        })
                                    );
                                }
                            }}
                        >
                            Terms of Service
                        </Button>{' '}
                        and{' '}
                        <Button
                            {...(!window.isNativeApp && { as: 'a' })}
                            to={privacyPolicy}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block underline"
                            onClick={() => {
                                if (window.isNativeApp) {
                                    window.ReactNativeWebView?.postMessage(
                                        JSON.stringify({
                                            navigate: 'SignupWebview',
                                            url: privacyPolicy,
                                        })
                                    );
                                }
                            }}
                        >
                            Privacy Policy
                        </Button>
                        .
                    </p>
                    {!isSmallScreen && (
                        <div className="w-full md:max-w-[250px] md:self-center">
                            <Button
                                disabled={
                                    !(zip.value === validDeliveryDays?.zip && zip.valid && email.valid) ||
                                    disableContinue ||
                                    !!custExistsError
                                }
                                size="md"
                                submitType
                            >
                                <span className="flex items-center">
                                    {noAvailableDays && zip.value === zip.waitListed && (
                                        <CheckIcon className="mb-[3px] mr-5 h-[22px] w-[22px]" />
                                    )}
                                    {ctaText}
                                </span>
                            </Button>
                        </div>
                    )}

                    <p className="my-10 text-center font-grotesk-bold text-body-mobile-sm">
                        Already have an account?{' '}
                        <Button
                            {...(!window.isNativeApp && { as: 'a' })}
                            to={authenticationPage}
                            className="link inline-block"
                            onClick={() => {
                                if (window.isNativeApp) {
                                    window.ReactNativeWebView?.postMessage(
                                        JSON.stringify({
                                            navigate: 'Login',
                                        })
                                    );
                                }
                            }}
                        >
                            Sign in
                        </Button>
                    </p>
                </form>
            </section>

            {inAssortmentPreview && !inPageOnlyTreatment ? (
                <AssortmentPreviewModule />
            ) : (
                <>
                    <HowItWorks />
                    <ProductPreviewWall />
                    {inPageOnlyTreatment && (
                        <div className="mx-auto max-w-[300px] pb-20">
                            <Button
                                variant="secondary"
                                as="link"
                                to="/grocery-and-produce-delivery-sneak-peek"
                                onClick={() =>
                                    trackEvent('grocery preview module cta clicked', {
                                        location: 'lead capture page',
                                    })
                                }
                            >
                                Preview Our Groceries
                            </Button>
                        </div>
                    )}
                </>
            )}

            <CustomerReviews />
            <LeadCapFAQS inPlusMembershipSignupOnTreatment={inPlusMembershipSignupOnTreatment} />
            {isSmallScreen && (
                <SignupFooter
                    submitButtonType="button"
                    onClick={handleSubmit}
                    isSubmitButtonDisabled={
                        !(zip.value === validDeliveryDays?.zip && zip.valid && email.valid) ||
                        disableContinue ||
                        !!custExistsError
                    }
                    hideInfoBubble
                />
            )}
        </main>
    );
}

export { Form as Component };
